import React, { useEffect, useState } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tabs,
  Tab,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import { supabase } from '../../config/supabase'
import { useAuth } from '../../hooks/useAuth'

interface Claim {
  id: string
  user_id: string
  undertaker_id?: string
  chain_id?: string
  status: 'pending' | 'approved' | 'denied'
  requested_at: string
  reviewed_at?: string
  notes?: string
  user_email: string
  undertaker_name?: string
  undertaker_city?: string
  undertaker_phone?: string
  chain_name?: string
  chain_location_count?: number
  claim_type: 'location' | 'chain'
  reviewer_email?: string
}

export default function AdminClaims() {
  const { user, isAdmin } = useAuth()
  const [claims, setClaims] = useState<Claim[]>([])
  const [filteredClaims, setFilteredClaims] = useState<Claim[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState(0) // 0: pending, 1: approved, 2: denied

  // Review dialog
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false)
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null)
  const [reviewAction, setReviewAction] = useState<'approve' | 'deny'>('approve')
  const [reviewNotes, setReviewNotes] = useState('')
  const [reviewing, setReviewing] = useState(false)

  useEffect(() => {
    if (isAdmin) {
      fetchClaims()
    }
  }, [isAdmin])

  useEffect(() => {
    filterClaims()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claims, activeTab])

  const fetchClaims = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('claims_with_details')
        .select('*')
        .order('requested_at', { ascending: false })

      if (error) throw error
      
      // Map the view data to our interface
      const mappedClaims: Claim[] = (data || []).map((item: any) => ({
        id: item.id,
        user_id: item.user_id,
        undertaker_id: item.undertaker_id,
        chain_id: item.chain_id,
        status: item.status,
        requested_at: item.requested_at,
        reviewed_at: item.reviewed_at,
        notes: item.notes,
        user_email: item.user_email || '',
        undertaker_name: item.undertaker_name,
        undertaker_city: item.undertaker_city,
        undertaker_phone: item.undertaker_phone,
        chain_name: item.chain_name,
        chain_location_count: item.chain_location_count,
        claim_type: item.chain_id ? 'chain' as const : 'location' as const,
        reviewer_email: item.reviewer_email,
      }))
      
      setClaims(mappedClaims)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const filterClaims = () => {
    let status: string
    switch (activeTab) {
      case 0:
        status = 'pending'
        break
      case 1:
        status = 'approved'
        break
      case 2:
        status = 'denied'
        break
      default:
        status = 'pending'
    }
    setFilteredClaims(claims.filter(c => c.status === status))
  }

  const handleReviewClick = (claim: Claim, action: 'approve' | 'deny') => {
    setSelectedClaim(claim)
    setReviewAction(action)
    setReviewNotes('')
    setReviewDialogOpen(true)
  }

  const handleReviewSubmit = async () => {
    if (!selectedClaim || !user) return

    try {
      setReviewing(true)
      setError(null)
      setSuccess(null)

      const newStatus = reviewAction === 'approve' ? 'approved' : 'denied'

      const { error } = await supabase
        .from('claims')
        .update({
          status: newStatus,
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
          notes: reviewNotes || null,
        })
        .eq('id', selectedClaim.id)

      if (error) throw error

      // If approved, update the undertaker or chain
      if (reviewAction === 'approve') {
        if (selectedClaim.claim_type === 'location' && selectedClaim.undertaker_id) {
          // Update undertaker for location claims
          const { error: undertakerError } = await supabase
            .from('undertakers')
            .update({
              claimed_by: selectedClaim.user_id,
              claim_approved: true,
              claim_approved_at: new Date().toISOString(),
            })
            .eq('id', selectedClaim.undertaker_id)

          if (undertakerError) throw undertakerError
        } else if (selectedClaim.claim_type === 'chain' && selectedClaim.chain_id) {
          // Update chain for chain claims
          const { error: chainError } = await supabase
            .from('chains')
            .update({
              claimed_by: selectedClaim.user_id,
              claim_approved: true,
              claim_approved_at: new Date().toISOString(),
            })
            .eq('id', selectedClaim.chain_id)

          if (chainError) throw chainError
        }
      }

      setSuccess(`Tilknytning ${reviewAction === 'approve' ? 'godkendt' : 'afvist'}!`)
      setReviewDialogOpen(false)
      setSelectedClaim(null)
      setReviewNotes('')
      await fetchClaims()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setReviewing(false)
    }
  }

  if (!isAdmin) {
    return (
      <Alert severity="error">
        Du har ikke tilladelse til at se denne side. Kun administratorer kan administrere claims.
      </Alert>
    )
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Administrer claims
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Godkend eller afvis brugernes anmodninger om at administrere bedemænd
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`Afventer (${claims.filter(c => c.status === 'pending').length})`} />
          <Tab label={`Godkendt (${claims.filter(c => c.status === 'approved').length})`} />
          <Tab label={`Afvist (${claims.filter(c => c.status === 'denied').length})`} />
        </Tabs>

        {filteredClaims.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Ingen claims i denne kategori
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Bruger</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Navn</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Detaljer</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Anmodet</TableCell>
                  {activeTab !== 0 && <TableCell sx={{ fontWeight: 600 }}>Behandlet</TableCell>}
                  {activeTab !== 0 && <TableCell sx={{ fontWeight: 600 }}>Behandlet Af</TableCell>}
                  <TableCell sx={{ fontWeight: 600 }}>Noter</TableCell>
                  {activeTab === 0 && <TableCell sx={{ fontWeight: 600, width: 120 }} align="right">Handling</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClaims.map((claim) => (
                  <TableRow key={claim.id} hover>
                    <TableCell>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: claim.claim_type === 'chain' ? 'primary.main' : 'grey.300',
                          color: claim.claim_type === 'chain' ? 'white' : 'text.primary',
                          display: 'inline-block',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {claim.claim_type === 'chain' ? 'KÆDE' : 'LOKATION'}
                      </Box>
                    </TableCell>
                    <TableCell>{claim.user_email}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {claim.claim_type === 'chain' ? claim.chain_name : claim.undertaker_name}
                    </TableCell>
                    <TableCell>
                      {claim.claim_type === 'chain' 
                        ? `${claim.chain_location_count || 0} lokationer`
                        : claim.undertaker_city
                      }
                    </TableCell>
                    <TableCell>
                      {new Date(claim.requested_at).toLocaleDateString('da-DK', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </TableCell>
                    {activeTab !== 0 && (
                      <TableCell>
                        {claim.reviewed_at ? new Date(claim.reviewed_at).toLocaleDateString('da-DK', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        }) : '-'}
                      </TableCell>
                    )}
                    {activeTab !== 0 && <TableCell>{claim.reviewer_email || '-'}</TableCell>}
                    <TableCell>{claim.notes || '-'}</TableCell>
                    {activeTab === 0 && (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleReviewClick(claim, 'approve')}
                          title="Godkend"
                        >
                          <CheckCircleIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleReviewClick(claim, 'deny')}
                          title="Afvis"
                        >
                          <CancelIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {reviewAction === 'approve' ? 'Godkend Tilknytning' : 'Afvis Tilknytning'}
        </DialogTitle>
        <DialogContent>
          {selectedClaim && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Bruger:</strong> {selectedClaim.user_email}
                <br />
                <strong>Type:</strong> {selectedClaim.claim_type === 'chain' ? 'Kæde' : 'Lokation'}
                <br />
                <strong>Navn:</strong>{' '}
                {selectedClaim.claim_type === 'chain' 
                  ? selectedClaim.chain_name 
                  : `${selectedClaim.undertaker_name} (${selectedClaim.undertaker_city})`
                }
                {selectedClaim.claim_type === 'chain' && selectedClaim.chain_location_count && (
                  <>
                    <br />
                    <strong>Lokationer:</strong> {selectedClaim.chain_location_count}
                  </>
                )}
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Noter (valgfrit)"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Tilføj en note til brugeren..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)} disabled={reviewing}>
            Annuller
          </Button>
          <Button
            onClick={handleReviewSubmit}
            variant="contained"
            color={reviewAction === 'approve' ? 'success' : 'error'}
            disabled={reviewing}
            startIcon={reviewing ? <CircularProgress size={20} /> : null}
          >
            {reviewing ? 'Behandler...' : (reviewAction === 'approve' ? 'Godkend' : 'Afvis')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

